import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { useAdminAuth, API_BASE } from "@/admin/AdminAuthContext";
import { downloadCsv, fmtDate } from "@/admin/utils";

const cedulaLabel = (c) => {
  switch (c) {
    case "tiene_a":
      return "Tiene Cédula A";
    case "tiene_a1":
      return "Tiene Cédula A1";
    case "tiene_b":
      return "Tiene Cédula B";
    case "en_tramite":
      return "En trámite";
    case "sin_cedula":
      return "Sin cédula";
    default:
      return c || "—";
  }
};

const expLabel = (e) => {
  switch (e) {
    case "ninguna":
      return "Sin experiencia";
    case "1-3":
      return "1 - 3 años";
    case "3-5":
      return "3 - 5 años";
    case "5+":
      return "Más de 5 años";
    default:
      return e || "—";
  }
};

const AdminApplications = () => {
  const { authedAxios } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    authedAxios
      .get(`${API_BASE}/applications?limit=500`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.trim().toLowerCase();
    return items.filter((a) =>
      [
        a.name,
        a.email,
        a.phone,
        a.city,
        cedulaLabel(a.cedula_status),
        expLabel(a.experience),
        a.motivation,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t))
    );
  }, [items, q]);

  const exportCsv = () => {
    downloadCsv(
      `emb-applications-${new Date().toISOString().slice(0, 10)}.csv`,
      filtered,
      [
        { label: "Fecha", value: (r) => fmtDate(r.created_at) },
        { label: "Nombre", value: (r) => r.name },
        { label: "Email", value: (r) => r.email },
        { label: "Teléfono", value: (r) => r.phone },
        { label: "Ciudad", value: (r) => r.city },
        { label: "Cédula", value: (r) => cedulaLabel(r.cedula_status) },
        { label: "Experiencia", value: (r) => expLabel(r.experience) },
        { label: "Motivación", value: (r) => r.motivation || "" },
      ]
    );
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-applications-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
            Aplicaciones · Únete al equipo
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-light leading-tight">
            <span className="italic text-[#E6D5B8]">{items.length}</span>{" "}
            candidatos
          </h1>
        </div>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="applications-export-btn"
          className="inline-flex items-center gap-2 bg-[#E6D5B8] hover:bg-[#FBEFD3] disabled:opacity-50 disabled:cursor-not-allowed text-[#0B0410] px-5 py-3 text-xs tracking-luxury uppercase font-semibold rounded-sm transition-colors"
        >
          <Download size={14} />
          <span>Exportar CSV</span>
        </button>
      </div>

      <div className="relative mb-6">
        <Search
          size={14}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A397B4]"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, ciudad, cédula o experiencia..."
          data-testid="applications-search"
          className="w-full pl-11 pr-4 py-3 bg-[#150A1F] border border-[#2A0A4A] focus:border-[#E6D5B8] outline-none rounded-sm text-sm text-[#F9F6F0] placeholder:text-[#A397B4]/55"
        />
      </div>

      {loading && (
        <div className="text-sm text-[#F9F6F0]/50 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando...
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm p-10 text-center text-sm text-[#F9F6F0]/55">
          {items.length === 0
            ? "Aún no hay aplicaciones recibidas."
            : "Ninguna aplicación coincide con tu búsqueda."}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="bg-[#150A1F] border border-[#2A0A4A] rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr_120px] text-[10px] tracking-luxury uppercase text-[#A397B4] px-5 py-3 border-b border-white/10 bg-white/[0.02]">
            <div>Nombre</div>
            <div>Email</div>
            <div>Ciudad</div>
            <div>Cédula</div>
            <div>Experiencia</div>
            <div className="text-right">Fecha</div>
          </div>

          <div className="divide-y divide-white/[0.07]">
            {filtered.map((a) => {
              const isOpen = expanded === a.id;
              return (
                <div
                  key={a.id}
                  data-testid={`application-row-${a.id}`}
                  className={`transition-colors ${isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : a.id)}
                    className="w-full text-left grid grid-cols-1 md:grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr_120px] gap-y-1 gap-x-4 px-5 py-4 items-center"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? (
                        <ChevronDown size={14} className="text-[#E6D5B8] shrink-0" />
                      ) : (
                        <ChevronRight size={14} className="text-[#A397B4] shrink-0" />
                      )}
                      <span className="font-display text-base truncate">
                        {a.name}
                      </span>
                    </div>
                    <a
                      href={`mailto:${a.email}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-[#F9F6F0]/75 hover:text-[#E6D5B8] truncate"
                    >
                      {a.email}
                    </a>
                    <div className="text-xs text-[#F9F6F0]/65">{a.city}</div>
                    <div className="text-xs text-[#F9F6F0]/65">
                      {cedulaLabel(a.cedula_status)}
                    </div>
                    <div className="text-xs text-[#F9F6F0]/65">
                      {expLabel(a.experience)}
                    </div>
                    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] md:text-right">
                      {fmtDate(a.created_at)}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pl-12 border-t border-white/5 pt-4 space-y-4">
                      <div>
                        <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-1">
                          Teléfono
                        </div>
                        <a
                          href={`tel:${a.phone}`}
                          className="text-sm text-[#F9F6F0]/85 hover:text-[#E6D5B8]"
                        >
                          {a.phone}
                        </a>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-1">
                          Motivación
                        </div>
                        <div className="text-sm text-[#F9F6F0]/80 whitespace-pre-wrap font-light leading-relaxed">
                          {a.motivation || (
                            <em className="text-[#F9F6F0]/40">
                              Sin texto de motivación.
                            </em>
                          )}
                        </div>
                      </div>
                      <div className="pt-1 flex gap-3 flex-wrap">
                        <a
                          href={`mailto:${a.email}`}
                          className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] border border-[#E6D5B8]/30 hover:border-[#E6D5B8] px-3 py-1.5 rounded-sm"
                        >
                          Responder por email
                        </a>
                        <a
                          href={`https://wa.me/${a.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] border border-[#E6D5B8]/30 hover:border-[#E6D5B8] px-3 py-1.5 rounded-sm"
                        >
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApplications;
